import { apiStatic } from 'AurionCR/components';
import { queryEncoderObject } from 'components/helpers';
import { API_LABELS, API_LANGUAGES } from 'services/labels';
import { API_RATING_PROCESS } from 'services/rating-process';
import { RatingProcessData, TranslateFunction } from './types';

// Step type 0 select fields (from main.ts)
const RPStepType0Select = [
  'discussionDate',
  'requestMaterialsDate',
  'responseMaterialsDate',
  'methodologyID',
  'subMethodologyID',
  'headOfFieldUserID',
  'teamLeaderUserID',
  'analystUserID',
];

/**
 * Loads rating process data by ID
 */
export const loadRatingProcessData = async (
  ratingProcessID: number,
): Promise<RatingProcessData> => {
  const {
    data: { value },
  } = await apiStatic.post(
    API_RATING_PROCESS.GET_ALL_DYNAMIC,
    queryEncoderObject({
      select: [
        'id',
        'ratedEntity.name as ratedEntityName',
        'ratingProcessType.title as ratingProcessTypeTitle',
        'ratingProcessDocumentations.Count() as ratingProcessDocumentationsCount',
        `ratingProcessSteps
          .OrderBy(o=>o.ratingProcessStepTypeID)
          .Select(s=>new {${[
            's.id',
            's.ratingProcessID',
            's.ratingProcessStepTypeID',
            's.ratingProcessStepType.title as ratingProcessStepTypeTitle',
          ].join()}}).toList() as ratingProcessSteps`.replace(/  +|\r\n|\n|\r/gm, ''),
        ...RPStepType0Select,
      ].join(),
      filter: `id==${ratingProcessID}`,
    }),
  );

  const main = value[0];
  if (!main) {
    throw new Error(`Rating process with ID ${ratingProcessID} not found`);
  }

  return main as RatingProcessData;
};

/**
 * Loads current language from localStorage or gets default
 */
export const loadCurrentLanguage = async (): Promise<number> => {
  const LANGUAGE_LOCAL_STORE_NAME = (userID?: string) =>
    userID ? `language_${userID}` : 'language';

  const storedLangID = localStorage.getItem(LANGUAGE_LOCAL_STORE_NAME());

  const {
    data: { value: languages },
  } = await apiStatic.post(
    API_LANGUAGES.GET_ALL_DYNAMIC,
    queryEncoderObject({
      select: ['id', 'title'].join(),
      filter: 'isActive==true',
      orderBy: 'rank',
    }),
  );

  if (!languages || languages.length === 0) {
    throw new Error('No active languages found');
  }

  const language = storedLangID
    ? languages.find((lang: any) => String(lang.id) === storedLangID)
    : null;

  return (language || languages[0])?.id || languages[0]?.id;
};

/**
 * Loads labels for a specific language and creates translate function
 */
export const loadTranslateFunction = async (languageID: number): Promise<TranslateFunction> => {
  const {
    data: { value },
  } = await apiStatic.post(
    API_LABELS.GET_ALL_DYNAMIC,
    queryEncoderObject({
      filter: `languageID=="${languageID}"`,
      select: ['labelKey', 'title'].join(),
    }),
  );

  const labelsMap: Record<string, string> = {};
  if (value && Array.isArray(value)) {
    value.forEach((label: any) => {
      labelsMap[label.labelKey] = label.title || label.labelKey;
    });
  }

  return (key?: string) => {
    if (!key) return '';
    return labelsMap[key] || key;
  };
};
